import type { ComponentType } from 'react';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import request from 'lib/request';
import {
  MapSourcesResponse,
  ReferenceLocationBucket,
  ReferenceLocationResponse,
} from 'types/api-responses';
import { LocationAtom, MapUserConfig } from './types';
import { getQuarteredGeometry } from './utils';
import { getResources } from 'atoms/root';
import { CacheMapEntry } from 'lib/atoms';

const hoveredLocationsAtom = atom<number[]>([]);

const createAbortableLocationsAtom = (
  buckets?: (ReferenceLocationBucket & { count: number })[]
) => {
  const abortableAtom = atom<LocationAtom[]>([]);

  abortableAtom.onMount = (setAtom) => {
    const abortController = new AbortController();
    const referenceLocationAttrs = buckets?.map((b) => ({
      href: b.href,
      count: b.count,
    }));
    const promises = referenceLocationAttrs?.map(({ href, count }) =>
      request
        .get<ReferenceLocationResponse & { count: number }>(
          href,
          abortController
        )
        .then((el) => {
          if ('error' in el) return el;

          el['count'] = count;
          return el;
        })
    );

    const asyncAtoms = promises ? promises.map((promise) => atom(promise)) : [];
    setAtom(asyncAtoms);
    return () => abortController.abort();
  };
  return abortableAtom;
};

type Points = [[number, number], [number, number]];
const createBoundingBoxAtom = (
  locationIds: number[],
  href: string | undefined
) => {
  const boundingBoxAtom = atom<Points>([
    [0, 0],
    [0, 0],
  ]);

  boundingBoxAtom.onMount = (setAtom) => {
    const abortController = new AbortController();
    if (!href) return;
    const getBoundingBox = async () => {
      const response = await request.post<
        { points: Points },
        { referenceLocationIds: number[] }
      >(href, { referenceLocationIds: locationIds }, abortController);
      if (response.type === 'success') {
        setAtom(response.data.points);
      }
    };

    getBoundingBox();

    return () => abortController.abort();
  };

  return boundingBoxAtom;
};

const createAbortableQuarteredGeometryAtom = (
  location: ReferenceLocationResponse | null,
  quarterCalls: string[],
  href: string | undefined
) => {
  const abortableAtom = atom<GeoJSON.Geometry | undefined>(undefined);

  abortableAtom.onMount = (setAtom) => {
    const abortController = new AbortController();

    const getGeography = async () => {
      if (!location) return;
      if (!href) return;

      const result = await getQuarteredGeometry(
        location.id,
        quarterCalls,
        href,
        abortController
      );

      if (result.type === 'success') {
        setAtom(result.data.geometry);
      }
    };

    getGeography();
    return () => abortController.abort();
  };
  return abortableAtom;
};

// Shared across every map-source layer: the one /maps/sources fetch that yields
// all 16 tile-source URLs. Only ever holds a *successful* response — a failed or
// `{ error }` fetch is discarded (set back to undefined) so it is retried rather
// than cached, and a failed request never wedges every layer until a full reload.
let mapSourcesAtomPromise:
  Promise<CacheMapEntry<MapSourcesResponse>['data']> | undefined;

const createMapSourceAtom = (source: keyof MapSourcesResponse) => {
  // Async derived atom: it reads `getResources`, so jotai re-runs it when the
  // root resources land (fixing the first-load race where the layer mounted
  // before resources were ready and gave up forever). `unwrap` exposes it to
  // consumers as a plain `string | undefined`, preserving the previous
  // synchronous read contract (no Suspense boundary required).
  const sourceAtom = atom(async (get): Promise<string | undefined> => {
    const mapSourcesHref = getResources(get)?.maps.sources.href;
    if (!mapSourcesHref) return undefined;

    if (!mapSourcesAtomPromise) {
      mapSourcesAtomPromise = request.get<MapSourcesResponse>(mapSourcesHref);
    }

    let response;
    try {
      response = await mapSourcesAtomPromise;
    } catch {
      // Never let a rejected fetch stay memoized — clear it so the next
      // evaluation retries instead of the layer staying blank until reload.
      mapSourcesAtomPromise = undefined;
      return undefined;
    }

    if (!response || 'error' in response) {
      mapSourcesAtomPromise = undefined;
      return undefined;
    }

    return response[source];
  });

  // Keep the previously resolved URL visible while a re-resolve is in flight so
  // the layer does not flicker to blank when resources change.
  return unwrap(sourceAtom, (prev) => prev);
};

const mapDefaultConfig: MapUserConfig = {
  wells: {
    checked: false,
    options: {
      surfaceHole: false,
      bottomHole: false,
      stick: true,
      survey: true,
      vertical: false,
    },
  },
  wellsV2: {
    checked: true,
    options: {
      surfaceHole: false,
      bottomHole: false,
      stick: true,
      survey: true,
      vertical: false,
      horizontal: true,
      horizontalPermit: true,
      horizontalDuc: true,
      horizontalProducing: true,
      horizontalShutIn: true,
      horizontalExpiredPermit: true,
      horizontalInactive: true,
      horizontalPrePermit: true,
      horizontalApd: true,
      horizontalProposal: true,
      horizontalPlanned: true,
      verticalPermit: false,
      verticalDuc: false,
      verticalProducing: false,
      verticalShutIn: false,
      verticalExpiredPermit: false,
      verticalInactive: false,
      verticalPrePermit: false,
      verticalApd: false,
      verticalProposal: false,
      verticalPlanned: false,
      permit: true,
      duc: true,
      producing: true,
      shutIn: true,
      expiredPermit: true,
      inactive: true,
      prePermit: true,
      apd: true,
      proposal: true,
      planned: true,
    },
  },
  developmentAreas: {
    checked: true,
    options: {
      horizontalUnits: true,
      verticalUnits: false,
      fieldUnits: false,
      pricing: false,
    },
  },
  titleEntityAssets: {
    checked: false,
    options: {
      surfaceInterest: false,
      workingInterest: false,
      unleased: false,
      royaltyInterest: false,
    },
  },
  portfolioAssets: {
    checked: false,
  },
  basinTier: {
    checked: false,
    options: {
      basin: false,
      tier: false,
    },
  },
  scrollZoom: {
    checked: false,
  },
  popups: {
    checked: true,
  },
  focusAreas: {
    checked: false,
    selections: {},
  },
  pipelines: {
    checked: false,
    options: {
      gasOperating: false,
      gasUnderConstruction: false,
      gasPlannedOther: false,
      oilOperating: false,
      oilUnderConstruction: false,
      oilPlannedOther: false,
      productOperating: false,
      productConstruction: false,
      productPlannedOther: false,
      unknownOperating: false,
      unknownUnderConstruction: false,
      unknownPlannedOther: false,
    },
  },
  drillingRigs: {
    checked: false,
    options: {
      horizontalDrilling: false,
      horizontalDown: false,
      horizontalNotReporting: false,
      horizontalNotTracking: false,
      horizontalUnreportedStatus: false,
      verticalDrilling: false,
      verticalDown: false,
      verticalNotReporting: false,
      verticalNotTracking: false,
      verticalUnreportedStatus: false,
    },
  },
};

const getMapDefaultConfig = (
  configOverrides?: Partial<MapUserConfig>
): MapUserConfig => {
  return {
    ...mapDefaultConfig,
    ...configOverrides,
  };
};

const measureModeAtom = atom(false);
const measurePointsAtom = atom<[number, number][]>([]);
const measureFinishedAtom = atom(false);

/**
 * A colour-by rule recolouring the map's OWN well layers, expressed as plainly as the
 * tiles allow: one tile property, and a colour per value of it.
 *
 * Declared here rather than by whichever feature sets it, so `WellsV2Layer` stays
 * ignorant of who is driving it and the dependency points at the map.
 */
interface MapWellPaint {
  /** The `wells-v2` tile property to match on, e.g. `status`. */
  property: string;
  /** Colour per tile value. A value absent here keeps the map's own colour. */
  colors: Record<string, string>;
}

/**
 * The well recolouring in force, or null to leave the map's own colours alone. Null
 * everywhere nothing is driving it, so a map that never sets it is untouched.
 */
const mapWellPaintAtom = atom<MapWellPaint | null>(null);

/**
 * Statuses a feature must be one of to be drawn, or null for no narrowing. Combined
 * with the map's own per-status toggles rather than replacing them: this NARROWS what
 * the map shows, so a status either control hides stays hidden.
 */
const mapWellStatusFilterAtom = atom<string[] | null>(null);

/**
 * Something every map draws inside itself, contributed by a feature the map knows
 * nothing about. See `useMapOverlay`.
 */
interface MapOverlay {
  /** Stable identity. Registering the same id again replaces the earlier entry. */
  id: string;
  /** Rendered inside each `<Map>`, so it can use `useMap()`. */
  component: ComponentType;
}

/** What every map draws, in registration order. */
const mapOverlaysAtom = atom<MapOverlay[]>([]);

/**
 * The tools taking map clicks right now, one entry per tool per map — an id can repeat.
 * See `useMapClickOwner`.
 */
const mapClickOwnersAtom = atom<string[]>([]);

/**
 * True while a click on the map belongs to a tool rather than to the map's popups:
 * measuring, or anything registered with `useMapClickOwner`.
 */
const mapClicksTakenAtom = atom(
  (get) => get(measureModeAtom) || get(mapClickOwnersAtom).length > 0
);

export {
  createAbortableLocationsAtom,
  createAbortableQuarteredGeometryAtom,
  createBoundingBoxAtom,
  createMapSourceAtom,
  getMapDefaultConfig,
  hoveredLocationsAtom,
  mapClickOwnersAtom,
  mapClicksTakenAtom,
  mapOverlaysAtom,
  mapWellPaintAtom,
  mapWellStatusFilterAtom,
  measureModeAtom,
  measurePointsAtom,
  measureFinishedAtom,
  type MapOverlay,
  type MapWellPaint,
};
